import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useAppContextProvider from "../../AppContext/useAppContextProvider";
import {
  AddAlertNotifications,
  MoveDGMember,
} from "../../api/api";

type AlertNotificationsResult = {
  status: string;
  Msg?: string;
  errorCode?: string;
};

type Malfunction = {
  type: string;
  subscribe: boolean;
  notifyMe: boolean;
};

type AlertNotification = {
  email: string;
  notifications: Malfunction[]; // Directly include notifications
};

export const usePostAlertNotifications = () => {
  const queryClient = useQueryClient();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { getAccessTokenSilently } = useAuth0();

  const mutation = useMutation({
    mutationFn: ({
      token,
      requestData,
    }: {
      token: string;
      requestData: AlertNotification[];
    }): Promise<AlertNotificationsResult> =>
      AddAlertNotifications(token, requestData), // Pass requestData directly
    onSuccess: (data) => {
      if (data.status === "error") {
        setToastMessage(
          `Error code: ${data.errorCode}, Error msg: ${data.Msg}` ||
            `Error adding alert notifications`
        );
      } else {
        setToastMessage(
          "Alert notifications have been added successfully"
        );
      }
      setToastOpen(true);
    },
    onError: (error) => {
      console.error(
        "Error adding alert notifications:",
        error
      );
      setToastMessage("Error adding alert notifications");
      setToastOpen(true);
    },
  });

  const mutateWithToken = useCallback(
    async (
      requestData: AlertNotification[],
      options?: {
        onSuccess?: (
          data: AlertNotificationsResult
        ) => void;
        onError?: (error: any) => void;
        onSettled?: () => void;
      }
    ) => {
      const token = await getAccessTokenSilently();

      mutation.mutate(
        {
          token,
          requestData, // Pass requestData directly
        },
        {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
          onSettled: options?.onSettled,
        }
      );
    },
    [getAccessTokenSilently, mutation]
  );

  return { ...mutation, mutate: mutateWithToken };
};
